// src/pages/Problems/ProblemsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Paper, Box, Grid, Card, CardContent, CardActions,
  Button, Chip, Divider, Avatar, Rating, Tabs, Tab, Skeleton,
  Alert, CircularProgress, IconButton, Collapse, List, ListItem,
  ListItemText, ListItemAvatar, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Stack, Pagination, Breadcrumbs, Link, useMediaQuery,
  useTheme, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  ExpandMore, Assignment, ChatBubbleOutline, LocalOffer,
  TrendingUp, Article, Visibility, ThumbUp, Comment,
  Send, Person, CalendarToday, Business, Category,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import api from '../../services/api';

const formatDate = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const RENGIGS = {
  green: '#2E7D32',
  lightGreen: '#4CAF50',
  lime: '#CDDC39',
  dark: '#1A2A3A',
  light: '#F8FAFC',
  border: '#E1E9F0',
  gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%)',
};

const ProblemsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  
  // ✅ Local authentication check to prevent premature redirect
  const [authChecked, setAuthChecked] = useState(false);

  // ─── All hooks must be called unconditionally at the top ─────────────────
  const [mainTab, setMainTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [myProblems, setMyProblems] = useState([]);
  const [expandedProblem, setExpandedProblem] = useState(null);
  const [taggedSolutions, setTaggedSolutions] = useState({});
  const [bids, setBids] = useState({});

  const [industryProblems, setIndustryProblems] = useState([]);
  const [challengesPage, setChallengesPage] = useState(1);
  const [challengesTotal, setChallengesTotal] = useState(0);

  const [articles, setArticles] = useState([]);
  const [articlesPage, setArticlesPage] = useState(1);
  const [articlesTotal, setArticlesTotal] = useState(0);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);

  // ─── Authentication check after mount (safe) ────────────────────────────
  useEffect(() => {
    // Wait for Redux to initialize or check token directly
    const token = localStorage.getItem('access_token');
    const isActuallyAuthenticated = isAuthenticated || !!token;
    
    if (!isActuallyAuthenticated) {
      navigate('/login');
    }
    setAuthChecked(true);
  }, [isAuthenticated, navigate]);

  // ─── Data fetching callbacks (defined unconditionally) ────────────────────
  const fetchMyProblems = useCallback(async () => {
    try {
      const data = await api.get('/problems/my-problems/?page=1&page_size=20');
      setMyProblems(data.results || []);
    } catch (err) {
      console.error('Failed to fetch my problems:', err);
    }
  }, []);

  const fetchTaggedSolutions = async (problemId) => {
    try {
      const data = await api.get(`/problems/${problemId}/tagged-solutions/`);
      setTaggedSolutions(prev => ({ ...prev, [problemId]: data }));
    } catch (err) {
      console.error('Failed to fetch tagged solutions:', err);
    }
  };

  const fetchBids = async (problemId) => {
    try {
      const data = await api.get(`/problems/${problemId}/bids/`);
      setBids(prev => ({ ...prev, [problemId]: data }));
    } catch (err) {
      console.error('Failed to fetch bids:', err);
    }
  };

  const handleExpandProblem = (problemId) => {
    if (expandedProblem === problemId) {
      setExpandedProblem(null);
    } else {
      setExpandedProblem(problemId);
      if (!taggedSolutions[problemId]) fetchTaggedSolutions(problemId);
      if (!bids[problemId]) fetchBids(problemId);
    }
  };

  const fetchIndustryChallenges = useCallback(async (page = 1) => {
    try {
      const params = { page, page_size: 6, ordering: '-created_at' };
      const data = await api.get('/problems/', params);
      setIndustryProblems(data.results || []);
      setChallengesTotal(data.count || 0);
    } catch (err) {
      console.error('Failed to fetch industry challenges:', err);
    }
  }, []);

  const fetchArticles = useCallback(async (page = 1) => {
    try {
      // Replace with real endpoint when available
      const mockArticles = [
        { id: 1, title: 'How to reduce supply chain costs', excerpt: 'Practical tips...', created_at: new Date().toISOString(), author: 'John Doe', image: null, content: 'Full article...' },
        { id: 2, title: 'AI in procurement', excerpt: 'Leveraging AI to find vendors...', created_at: new Date().toISOString(), author: 'Jane Smith', image: null, content: '...' },
        { id: 3, title: 'Sustainable sourcing strategies', excerpt: 'Integrate ESG criteria...', created_at: new Date().toISOString(), author: 'Alice Johnson', image: null, content: '...' },
      ];
      setArticles(mockArticles);
      setArticlesTotal(mockArticles.length);
    } catch (err) {
      console.error('Failed to fetch articles:', err);
    }
  }, []);

  // ─── Load data only when authenticated and token exists ─────────────────
  useEffect(() => {
    if (!authChecked) return; // Wait for auth check to complete
    const token = localStorage.getItem('access_token');
    const isActuallyAuthenticated = isAuthenticated || !!token;
    if (!isActuallyAuthenticated) return;
    
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchMyProblems(),
          fetchIndustryChallenges(),
          fetchArticles(),
        ]);
      } catch (err) {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [authChecked, isAuthenticated, fetchMyProblems, fetchIndustryChallenges, fetchArticles]);

  // ─── Handlers (called conditionally but they are just functions) ──────────
  const handleTabChange = (event, newValue) => setMainTab(newValue);
  const handleChallengesPageChange = (event, value) => {
    setChallengesPage(value);
    fetchIndustryChallenges(value);
  };
  const handleArticlesPageChange = (event, value) => {
    setArticlesPage(value);
    fetchArticles(value);
  };
  const openArticleDialog = (article) => {
    setSelectedArticle(article);
    setArticleDialogOpen(true);
  };

  // ─── Authentication guard after all hooks, but before render ─────────────
  if (!authChecked) {
    // Still checking authentication – show nothing or a spinner
    return null;
  }
  
  const token = localStorage.getItem('access_token');
  const isActuallyAuthenticated = isAuthenticated || !!token;
  if (!isActuallyAuthenticated) {
    return null; // Already redirecting via useEffect
  }

  // ─── Loading state (only for authenticated users) ─────────────────────────
  if (loading) {
    return (
      <Container sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={400} />
      </Container>
    );
  }

  // ─── Render (unchanged from original) ─────────────────────────────────────
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, color: RENGIGS.dark, mb: 1 }}>
          Problem & Market Intelligence
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your challenges, explore industry trends, and share practical knowledge
        </Typography>
      </Box>

      {/* Main Tabs */}
      <Paper elevation={0} sx={{ border: `1px solid ${RENGIGS.border}`, borderRadius: 2, mb: 3 }}>
        <Tabs
          value={mainTab}
          onChange={handleTabChange}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons="auto"
          sx={{
            bgcolor: RENGIGS.light,
            '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', fontSize: '0.9rem' },
            '& .Mui-selected': { color: `${RENGIGS.green} !important` },
            '& .MuiTabs-indicator': { bgcolor: RENGIGS.green },
          }}
        >
          <Tab label="My Problems & Bids" />
          <Tab label="Industry Challenges" />
          <Tab label="Practical Experience" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* SECTION 1: My Problems & Bids */}
          {mainTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: RENGIGS.dark }}>
                  My Challenges
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Assignment />}
                  onClick={() => navigate('/problems/create')}
                  sx={{ bgcolor: RENGIGS.green, textTransform: 'none', borderRadius: 2 }}
                >
                  Post New Challenge
                </Button>
              </Box>

              {myProblems.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  You haven't posted any problems yet. Click "Post New Challenge" to get started.
                </Alert>
              ) : (
                <Grid container spacing={3}>
                  {myProblems.map((problem) => (
                    <Grid item xs={12} key={problem.id}>
                      <Card sx={{ borderRadius: 2, border: `1px solid ${RENGIGS.border}`, transition: '0.2s', '&:hover': { boxShadow: 3 } }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: RENGIGS.dark }}>
                                {problem.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Posted {formatDate(problem.created_at)}
                              </Typography>
                            </Box>
                            <Chip
                              label={problem.status || 'Open'}
                              size="small"
                              sx={{ bgcolor: RENGIGS.lightGreen, color: '#fff', fontWeight: 600 }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ mt: 2, color: '#475569', lineHeight: 1.6 }}>
                            {problem.description?.substring(0, 200)}...
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => handleExpandProblem(problem.id)}
                            sx={{ mt: 1, textTransform: 'none', color: RENGIGS.green }}
                            endIcon={<ExpandMore sx={{ transform: expandedProblem === problem.id ? 'rotate(180deg)' : 'none' }} />}
                          >
                            {expandedProblem === problem.id ? 'Hide Details' : 'View Tagged Solutions & Bids'}
                          </Button>
                        </CardContent>
                        <Collapse in={expandedProblem === problem.id}>
                          <Divider />
                          <CardContent sx={{ bgcolor: RENGIGS.light }}>
                            {/* Tagged Solutions */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                              <LocalOffer fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                              Tagged Solutions
                            </Typography>
                            {!taggedSolutions[problem.id] ? (
                              <CircularProgress size={24} />
                            ) : taggedSolutions[problem.id]?.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">No solutions tagged yet.</Typography>
                            ) : (
                              <List dense>
                                {taggedSolutions[problem.id].map((sol) => (
                                  <ListItem key={sol.solution_id} button onClick={() => navigate(`/solutions/${sol.solution_id}`)}>
                                    <ListItemAvatar><Avatar src={sol.logo}><Business /></Avatar></ListItemAvatar>
                                    <ListItemText
                                      primary={sol.title}
                                      secondary={`Provider: ${sol.provider_name} · Rating: ${sol.rating_avg || 'N/A'}`}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            )}

                            <Divider sx={{ my: 2 }} />

                            {/* Bids / EOIs */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                              <ChatBubbleOutline fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                              Bids & EOIs Received
                            </Typography>
                            {!bids[problem.id] ? (
                              <CircularProgress size={24} />
                            ) : bids[problem.id]?.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">No bids yet.</Typography>
                            ) : (
                              <List dense>
                                {bids[problem.id].map((bid) => (
                                  <ListItem key={bid.id}>
                                    <ListItemText
                                      primary={bid.supplier_name || bid.supplier_email}
                                      secondary={`Amount: ${bid.amount || 'N/A'} · Status: ${bid.status || 'Pending'}`}
                                    />
                                    <Button size="small" variant="outlined" sx={{ borderColor: RENGIGS.green, color: RENGIGS.green }}>
                                      View
                                    </Button>
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </CardContent>
                        </Collapse>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* SECTION 2: Industry Challenges (Other Organizations) */}
          {mainTab === 1 && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: RENGIGS.dark, mb: 3 }}>
                Industry Challenges by Sector
              </Typography>
              <Grid container spacing={3}>
                {industryProblems.map((problem) => (
                  <Grid item xs={12} md={6} key={problem.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2, border: `1px solid ${RENGIGS.border}` }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Avatar sx={{ bgcolor: RENGIGS.green, width: 32, height: 32 }}>
                            <Business fontSize="small" />
                          </Avatar>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: RENGIGS.dark }}>
                            {problem.organization_name || 'Anonymous Organization'}
                          </Typography>
                          <Chip size="small" label={problem.category_name || 'General'} variant="outlined" />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, cursor: 'pointer' }} onClick={() => navigate(`/problems/${problem.id}`)}>
                          {problem.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {problem.description?.substring(0, 120)}...
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Chip icon={<Comment />} label={`${problem.comments_count || 0} comments`} size="small" />
                          <Chip icon={<LocalOffer />} label={`${problem.tagged_solutions_count || 0} solutions`} size="small" />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(problem.created_at)}
                          </Typography>
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button size="small" onClick={() => navigate(`/problems/${problem.id}`)} sx={{ color: RENGIGS.green }}>
                          View Challenge & Solutions
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {challengesTotal > 6 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination count={Math.ceil(challengesTotal / 6)} page={challengesPage} onChange={handleChallengesPageChange} color="primary" />
                </Box>
              )}
            </Box>
          )}

          {/* SECTION 3: Practical Experience (Articles & Blogs) */}
          {mainTab === 2 && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: RENGIGS.dark, mb: 3 }}>
                Sharing Practical Experience
              </Typography>
              <Grid container spacing={3}>
                {articles.map((article) => (
                  <Grid item xs={12} md={4} key={article.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2, border: `1px solid ${RENGIGS.border}` }}>
                      <Box sx={{ height: 140, bgcolor: RENGIGS.light, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {article.image ? (
                          <img src={article.image} alt={article.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <Article sx={{ fontSize: 60, color: RENGIGS.green }} />
                        )}
                      </Box>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{article.title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {article.excerpt?.substring(0, 100)}...
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                          <Person fontSize="small" />
                          <Typography variant="caption">{article.author || 'Admin'}</Typography>
                          <CalendarToday fontSize="small" sx={{ ml: 1 }} />
                          <Typography variant="caption">{formatDate(article.created_at)}</Typography>
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button size="small" onClick={() => openArticleDialog(article)} sx={{ color: RENGIGS.green }}>
                          Read More
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {articlesTotal > 6 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination count={Math.ceil(articlesTotal / 6)} page={articlesPage} onChange={handleArticlesPageChange} color="primary" />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Article Detail Dialog */}
      <Dialog open={articleDialogOpen} onClose={() => setArticleDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedArticle && (
          <>
            <DialogTitle sx={{ bgcolor: RENGIGS.gradient, color: 'white', fontWeight: 800 }}>
              {selectedArticle.title}
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Avatar><Person /></Avatar>
                <Box>
                  <Typography variant="subtitle2">{selectedArticle.author || 'Admin'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(selectedArticle.created_at)}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                {selectedArticle.content || selectedArticle.excerpt || 'Full content would appear here.'}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setArticleDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default ProblemsPage;